import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { GlowingEffect } from "../ui/glowing-effect";

const GuideStep = ({ onComplete }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [toggleOn, setToggleOn] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setToggleOn(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const GridItem = ({ area, number, title, description }) => {
    return (
      <Box
        className={`${area}`}
        sx={{
          listStyle: "none",
          minHeight: "6.5rem",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: "100%",
            borderRadius: "16px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "6px",
            "@media (min-width: 768px)": {
              borderRadius: "20px",
              padding: "8px",
            },
          }}
        >
          <GlowingEffect
            spread={32}
            glow={true}
            disabled={false}
            proximity={48}
            inactiveZone={0.01}
            borderWidth={1}
          />
          <Box
            sx={{
              position: "relative",
              display: "flex",
              height: "100%",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 2,
              overflow: "hidden",
              borderRadius: "10px",
              padding: 2,
              background: "rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(10px)",
              "@media (min-width: 768px)": {
                padding: 2.5,
              },
            }}
          >
            <Box sx={{ position: "relative", display: "flex", flexDirection: "column", flex: 1, justifyContent: "space-between", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Box 
                  sx={{ 
                    fontSize: "0.875rem", 
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    fontWeight: 600,
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                  }}
                >
                  {number}
                </Box>
              </Box>
              <Box sx={{ spaceY: 1, textAlign: "center" }}>
                <Typography
                  component="h3"
                  sx={{
                    paddingTop: "2px",
                    fontFamily: "system-ui, sans-serif",
                    fontSize: "1.125rem",
                    lineHeight: "1.25rem",
                    fontWeight: 600,
                    color: "#ffffff",
                    textAlign: "center",
                    "@media (min-width: 768px)": {
                      fontSize: "1.25rem",
                      lineHeight: "1.5rem",
                    },
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  component="p"
                  sx={{
                    fontFamily: "system-ui, sans-serif",
                    fontSize: "0.813rem",
                    lineHeight: "1rem",
                    color: "#9ca3af",
                    mt: 0.5,
                    textAlign: "center",
                    "@media (min-width: 768px)": {
                      fontSize: "0.875rem",
                      lineHeight: "1.25rem",
                    },
                  }}
                >
                  {description}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Container 
      maxWidth={false}
      sx={{ 
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 4, sm: 6, md: 8 },
        overflow: "hidden",
        maxHeight: "100vh",
      }}
    >
      {/* Main content */}
      <Box
        sx={{
          textAlign: "center",
          maxWidth: "1200px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 3, sm: 4 },
          height: "100%",
          maxHeight: "100%",
          overflow: "hidden",
        }}
      >
        {/* Hero toggle */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: { xs: 2, sm: 3 },
            animation: "fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(24px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          {/* 3D Toggle Switch */}
          <Box
            sx={{
              width: 120,
              height: 60,
              borderRadius: "30px",
              background: toggleOn 
                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                : "linear-gradient(135deg, #374151 0%, #1f2937 100%)",
              display: "flex",
              alignItems: "center",
              padding: "4px",
              boxShadow: toggleOn
                ? "inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 8px 32px rgba(16, 185, 129, 0.4), 0 4px 16px rgba(16, 185, 129, 0.2)"
                : "inset 0 2px 4px rgba(0, 0, 0, 0.3), 0 6px 24px rgba(0, 0, 0, 0.2), 0 2px 6px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              position: "relative",
              cursor: "pointer",
              transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
              "&:before": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: toggleOn ? "calc(100% - 56px)" : "4px",
                transform: "translateY(-50%)",
                width: "52px",
                height: "52px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ffffff 0%, #f1f5f9 50%, #e2e8f0 100%)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.8)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
              },
              "&:after": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: toggleOn ? "calc(100% - 56px)" : "4px",
                transform: "translateY(-50%)",
                width: "52px",
                height: "52px",
                borderRadius: "50%",
                background: "radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0) 70%)",
                transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
                pointerEvents: "none",
              },
            }}
          >
            {/* Inner glow effect */}
            <Box
              sx={{
                position: "absolute",
                inset: "2px",
                borderRadius: "28px",
                background: toggleOn
                  ? "linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)"
                  : "linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)",
                transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
              }}
            />
          </Box>
        </Box>

        {/* Title */}
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            color: "#ffffff",
            letterSpacing: "-0.04em",
            lineHeight: 1.1,
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            mb: { xs: 1, sm: 2 },
            animation: "fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(24px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          QA on Autopilot: 
          <Box
            component="span"
            sx={{
              color: toggleOn ? "#10b981" : "#6b7280",
              transition: "color 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
              ml: 1,
              position: "relative",
              "&:after": toggleOn ? {
                content: '""',
                position: "absolute",
                bottom: "-4px",
                left: "0",
                right: "0",
                height: "2px",
                background: "linear-gradient(90deg, #10b981, #059669)",
                borderRadius: "1px",
                animation: "slideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.8s both",
                "@keyframes slideIn": {
                  "0%": {
                    transform: "scaleX(0)",
                    transformOrigin: "left",
                  },
                  "100%": {
                    transform: "scaleX(1)",
                    transformOrigin: "left",
                  },
                },
              } : {},
            }}
          >
            {toggleOn ? "ON" : "OFF"}
          </Box>
        </Typography>

        {/* Bento Grid */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: "100%",
            maxHeight: { xs: "50vh", xl: "40vh" },
            overflow: "hidden",
            animation: "fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(24px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          <Box
            component="ul"
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: { xs: 1.5, sm: 2 },
              height: "100%",
              "@media (min-width: 768px)": {
                gridTemplateColumns: "repeat(2, 1fr)",
              },
              "@media (min-width: 1024px)": {
                gridTemplateColumns: "repeat(3, 1fr)",
              },
              "@media (min-width: 1280px)": {
                gridTemplateColumns: "repeat(3, 1fr)",
                gridTemplateRows: "minmax(120px, auto)",
              },
            }}
          >
            <GridItem
              area="xl:col-span-1"
              number="1"
              title="Record Looms of Your Product"
              description="Create Loom videos showcasing what to test"
            />
            <GridItem
              area="xl:col-span-1"
              number="2"
              title="Generate Playwright"
              description="Fume will generate an extensive test suite"
            />
            <GridItem
              area="xl:col-span-1"
              number="3"
              title="Run & Self Heal"
              description="Run the tests and watch Fume automatically maintain them"
            />
          </Box>
        </Box>
      </Box>

      {/* CTA Button */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "280px",
          marginTop: { xs: 4, sm: 6 },
          animation: "fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.4s both",
          "@keyframes fadeInUp": {
            "0%": {
              opacity: 0,
              transform: "translateY(24px)",
            },
            "100%": {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
      >
        <Button
          fullWidth
          onClick={onComplete}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            height: 48,
            borderRadius: 2,
            background: "#ffffff",
            border: "none",
            color: "#000000",
            fontSize: "1rem",
            fontWeight: 600,
            textTransform: "none",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            boxShadow: "0 4px 16px rgba(255, 255, 255, 0.2), 0 2px 8px rgba(255, 255, 255, 0.1)",
            transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
            "&:hover": {
              transform: "translateY(-2px) scale(1.02)",
              boxShadow: "0 8px 24px rgba(255, 255, 255, 0.25), 0 4px 12px rgba(255, 255, 255, 0.15)",
              background: "#f8fafc",
            },
            "&:active": {
              transform: "translateY(0) scale(1)",
              transition: "all 0.1s cubic-bezier(0.16, 1, 0.3, 1)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <span>Get Started</span>
            <Box
              sx={{
                transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                transform: isHovered ? "translateX(4px)" : "translateX(0)",
                fontSize: "1.1rem",
              }}
            >
              →
            </Box>
          </Box>
        </Button>
      </Box>
    </Container>
  );
};

export default GuideStep; 